import { TResolveMediaSrc, handlebars } from './handlebars';

export interface IHtmlTemplateMinimum {
  cssStyles?: string | null;
  bodyParts: string[];
}

export interface IHtmlTemplate extends IHtmlTemplateMinimum {
  marginTop?: number | null;
  marginBottom?: number | null;
  marginLeft?: number | null;
  marginRight?: number | null;
  headerTemplate?: string | null;
  footerTemplate?: string | null;
}

export const pdfTemplate2Html = ({ cssStyles, bodyParts }: IHtmlTemplateMinimum) => {
  const htmlContent = `
  <html>
    <head>
      <style>
        .pagebreak {
          break-inside: avoid;
        }

        tbody {page-break-inside: avoid; width: 100%;}

        ${cssStyles}
      </style>
    </head>
  <body>
    ${bodyParts.join('')}
  </body>
</html>`;

  const fontFamilies = extractGoogleFonts(htmlContent, cssStyles);
  return injectGoogleFontsLink(htmlContent, fontFamilies);
};

export const htmlTemplate2Html = ({
  cssStyles,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  headerTemplate,
  footerTemplate,
  bodyParts,
}: IHtmlTemplate) => {
  const htmlContent = `
  <html>
  <head>
    <script>
      window.onload = function() {
        var wrapOuter = document.getElementById('wrap-outer');
        if (!wrapOuter) return; // Guard against missing element

        // Style for potential centering
        wrapOuter.style.margin = '0 auto'; // Center block element
        // Ensure position is relative or absolute if needed for other elements, but auto margin handles centering
        // wrapOuter.style.position = 'relative'; // Or 'absolute' depending on layout needs

        function resize() {
          // Get the intended width in pixels (getBoundingClientRect includes zoom, so divide by current zoom)
          var currentZoom = parseFloat(wrapOuter.style.zoom) || 1;
          var intendedWidthPx = wrapOuter.getBoundingClientRect().width / currentZoom; 
          
          // Get the available viewport width
          var availableWidth = document.documentElement.clientWidth || document.body.clientWidth;
          var scale = 1; // Default scale is 1 (no scaling)

          // Only scale down if the viewport is narrower than the intended width
          if (availableWidth < intendedWidthPx) {
            scale = availableWidth / intendedWidthPx;
          }
          
          // Apply the calculated scale using zoom
          wrapOuter.style.zoom = scale; 
        }

        window.addEventListener("resize", resize);
        resize(); // Initial call
      }
    </script>
    <style>
      del {
        background: #ffc6c4;
        margin-right: 5px;
      }

      ins {
        background: #D1FFBD;
        text-decoration: none;
      }

      body, html {
        padding: 0px;
        margin: 0px;
      }
        
      #wrap-outer {
        width: 210mm;
        /* Removed position: absolute; Use margin: auto for centering */
        /* position: absolute; */ 
        /* Added margin auto for centering */
        margin-left: auto;
        margin-right: auto;
        /* Consider adding overflow: hidden if content might exceed bounds during scaling */
        overflow: hidden; 
      }

      #wrap-inner {
        box-sizing: border-box;
        margin-top: ${marginTop || 0}px;
        margin-bottom: ${marginBottom || 0}px;
        margin-left: ${marginLeft || 0}px;
        margin-right: ${marginRight || 0}px;
      }

      #header {
        position: absolute;
        top: 0px;
      }

      #footer {
        position: absolute;
        bottom: 0px;
      }

      ${cssStyles || ''}
    </style>
  </head>
  <body>
    <div id="wrap-outer">
      <div id="header">
        ${headerTemplate || ''}
      </div>
    <div id="wrap-inner">
      ${bodyParts.join('')}
    </div>
    <div id="footer">
      ${footerTemplate || ''}
    </div>
    </div>
  </body>
</html>
  `;

  const fontFamilies = extractGoogleFonts(htmlContent, cssStyles);
  console.log('detected fontFamilies', fontFamilies);
  return injectGoogleFontsLink(htmlContent, fontFamilies);
};

export async function generateHtmlForHtmlTemplate(template: IHtmlTemplate, input: any, resolveMediaSrc?: TResolveMediaSrc | null): Promise<string> {
  const renderedBodyParts = await Promise.all(template.bodyParts.map(async bp => handlebars(bp, input, resolveMediaSrc)));
  return htmlTemplate2Html({
    ...template,
    headerTemplate: template.headerTemplate ? await handlebars(template.headerTemplate, input, resolveMediaSrc) : null,
    footerTemplate: template.footerTemplate ? await handlebars(template.footerTemplate, resolveMediaSrc) : null,
    bodyParts: renderedBodyParts,
  });
}

export async function generateHtmlForPdfTemplate(
  template: IHtmlTemplateMinimum,
  input: any,
  resolveMediaSrc?: TResolveMediaSrc | null,
): Promise<string> {
  const renderedBodyParts = await Promise.all(template.bodyParts.map(async bp => handlebars(bp, input, resolveMediaSrc)));

  return pdfTemplate2Html({
    ...template,
    bodyParts: renderedBodyParts,
  });
}

// List of common generic font families to exclude when searching for Google Fonts
const GENERIC_FONT_FAMILIES = new Set([
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
  'arial',
  'helvetica',
  'times new roman',
  'courier new',
  'verdana',
  'georgia',
  'system-ui',
  '-apple-system',
  'blinkmacsystemfont',
  'segoe ui',
  'ubuntu',
  'cantarell',
  'noto sans',
  'oxygen',
  'fira sans',
  'droid sans',
  'helvetica neue', // Added more common system/default fonts
]);

/**
 * Extracts potential Google Font family names from HTML content and CSS styles.
 * @param htmlContent - The HTML content string.
 * @param cssStyles - Optional CSS styles string.
 * @returns An array of unique potential Google Font family names.
 */
export const extractGoogleFonts = (htmlContent: string, cssStyles?: string | null): string[] => {
  const fontFamilies = new Set<string>();
  // Regex to find font-family declarations in style attributes, style tags, and CSS rules
  const fontFamilyRegex = /font-family\s*:\s*([^;\}]+)/gi;

  const extractFonts = (content: string) => {
    let match;
    while ((match = fontFamilyRegex.exec(content)) !== null) {
      // Extract the font list, remove quotes, split by comma, trim whitespace
      const fonts = match[1]
        .replace(/['"]/g, '') // Remove single and double quotes
        .split(',')
        .map(font => font.trim().toLowerCase()) // Trim and convert to lower case for comparison
        .filter(font => font && !GENERIC_FONT_FAMILIES.has(font)); // Filter out empty strings and generic families

      fonts.forEach(font => fontFamilies.add(font));
    }
  };

  // Extract from inline styles and style tags in HTML
  extractFonts(htmlContent);

  // Extract from provided CSS styles
  if (cssStyles) {
    extractFonts(cssStyles);
  }

  // Convert back to original case (or title case as a guess) and return unique list
  // Ensure all parts of the font family name have an uppercase char
  return Array.from(fontFamilies).map(font =>
    font
      .split(/[-\s]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join('+'),
  );
};

/**
 * Injects Google Fonts <link> tags into the <head> of an HTML document.
 * @param htmlContent - The HTML content string.
 * @param fontFamilies - An array of Google Font family names.
 * @returns The HTML content with the Google Fonts links injected, or the original HTML if no fonts are provided or <head> is missing.
 */
export const injectGoogleFontsLink = (htmlContent: string, fontFamilies: string[]): string => {
  if (!fontFamilies || fontFamilies.length === 0) {
    return htmlContent;
  }

  // Create a separate <link> tag for each font family
  const linkTags = fontFamilies
    .map(font => {
      const encodedFont = font.replace(/ /g, '+');
      const googleFontsUrl = `https://fonts.googleapis.com/css2?family=${encodedFont}&display=swap`;
      return `<link href="${googleFontsUrl}" rel="stylesheet">\n`;
    })
    .join('');

  // Find the closing </head> tag (case-insensitive)
  const headEndIndex = htmlContent.toLowerCase().indexOf('</head>');

  if (headEndIndex !== -1) {
    // Inject the link tags before the closing </head> tag
    return htmlContent.slice(0, headEndIndex) + linkTags + htmlContent.slice(headEndIndex);
  } else {
    // If no </head> tag found, return the original HTML
    console.warn('injectGoogleFontsLink: Could not find </head> tag to inject Google Fonts links.');
    return htmlContent;
  }
};
